// src/components/store/sections/product-cards/PolaroidTapeProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingCart, Heart, Check, Sparkles } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const TAPE_THEMES = [
  {
    tapeBg: "bg-amber-200/80 border-amber-300/60",
    stageBg: "from-amber-50 to-orange-100/70",
    stampColor: "text-rose-600 border-rose-500/40 bg-rose-50",
    btnBg: "bg-slate-900 hover:bg-amber-600",
    tilt: "hover:-rotate-1",
  },
  {
    tapeBg: "bg-rose-200/80 border-rose-300/60",
    stageBg: "from-rose-50 to-pink-100/70",
    stampColor: "text-purple-700 border-purple-500/40 bg-purple-50",
    btnBg: "bg-slate-900 hover:bg-rose-600",
    tilt: "hover:rotate-1",
  },
  {
    tapeBg: "bg-cyan-200/80 border-cyan-300/60",
    stageBg: "from-cyan-50 to-sky-100/70",
    stampColor: "text-emerald-700 border-emerald-500/40 bg-emerald-50",
    btnBg: "bg-slate-900 hover:bg-sky-600",
    tilt: "hover:-rotate-1",
  },
  {
    tapeBg: "bg-violet-200/80 border-violet-300/60",
    stageBg: "from-violet-50 to-purple-100/70",
    stampColor: "text-indigo-700 border-indigo-500/40 bg-indigo-50",
    btnBg: "bg-slate-900 hover:bg-violet-600",
    tilt: "hover:rotate-1",
  },
];

export default function PolaroidTapeProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    added,
    wishlisted,
    setWishlisted,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = TAPE_THEMES[index % TAPE_THEMES.length];

  return (
    <div className="relative w-full h-full pt-3 select-none group">
      {/* Translucent Top Washi Tape Strip */}
      <div
        className={`absolute top-0 left-1/2 -translate-x-1/2 -rotate-3 z-20 w-24 h-6 ${theme.tapeBg} border shadow-xs backdrop-blur-xs pointer-events-none flex items-center justify-center`}
        style={{ clipPath: "polygon(4% 0%, 96% 0%, 100% 50%, 95% 100%, 5% 100%, 0% 50%)" }}
      >
        <span className="text-[8px] font-mono uppercase tracking-widest text-slate-600/80 font-bold">
          CURATED
        </span>
      </div>

      {/* Polaroid Instant Photo Frame */}
      <div
        className={`w-full h-full bg-[#fffdf9] rounded-2xl p-3.5 pb-4 border-2 border-slate-200/80 shadow-[0_12px_28px_rgba(0,0,0,0.08)] group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.14)] transition-all duration-300 ${theme.tilt} flex flex-col justify-between`}
      >
        <div>
          {/* Square Photo Stage */}
          <div
            className={`relative w-full h-48 rounded-xl bg-gradient-to-br ${theme.stageBg} border border-slate-200/60 overflow-hidden flex items-center justify-center p-3 shadow-inner`}
          >
            {/* Rubber Price Stamp */}
            <div
              className={`absolute top-2.5 left-2.5 z-10 px-2.5 py-0.5 rounded-md border-2 border-dashed -rotate-6 font-mono font-black text-xs shadow-2xs ${theme.stampColor}`}
            >
              ৳{currentPrice.toLocaleString()}
            </div>

            <button
              type="button"
              onClick={() => setWishlisted(!wishlisted)}
              className="absolute top-2.5 right-2.5 z-10 w-7 h-7 rounded-full bg-white/90 shadow-xs flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
            >
              <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
            </button>

            <Link href={productUrl} className="relative w-36 h-36 block">
              <Image
                src={productImg}
                alt={productName}
                fill
                unoptimized
                className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-xl"
              />
            </Link>
          </div>

          {/* Polaroid Caption Area */}
          <div className="pt-3.5 px-1 text-center space-y-1">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span>{categoryName}</span>
            </div>

            <Link href={productUrl} className="block">
              <h3 className="text-sm sm:text-base font-serif italic font-black text-slate-900 truncate hover:text-rose-600 transition-colors">
                {productName}
              </h3>
            </Link>

            <div className="flex items-center justify-center gap-1 pt-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-[10px] font-bold text-slate-400 ml-1">({rating.toFixed(1)})</span>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="pt-3 mt-2 border-t border-dashed border-slate-200 flex items-center justify-between gap-2">
          <div className="text-left">
            <span className="text-sm font-black text-slate-900 font-mono block leading-none">
              ৳{currentPrice.toLocaleString()}
            </span>
            {comparePrice > currentPrice && (
              <span className="text-[10px] text-slate-400 line-through font-mono">
                ৳{comparePrice.toLocaleString()}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className={`px-4 py-2 rounded-xl text-white font-black text-[10px] uppercase tracking-wider transition-all active:scale-95 cursor-pointer flex items-center gap-1.5 shadow-sm ${
              added ? "bg-emerald-600" : theme.btnBg
            }`}
          >
            {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
            <span>{added ? "Added" : "Snap Cart"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
